import { useId, useRef, useState, type KeyboardEvent } from "react";
import { Input } from "@agentsaas/ui";

/**
 * 请求头值输入框：输入 `{{secret:` 后弹出本租户密钥名补全（↑↓ 选择，Enter / Tab 接受，Esc 关闭），
 * 接受后替换为 `{{secret:NAME}}`。
 */

const TRIGGER = /\{\{secret:([A-Za-z0-9_]*)$/;

/** 光标前的未完成引用（没有则 null） */
export function pendingRef(text: string, caret: number): { start: number; partial: string } | null {
  const m = TRIGGER.exec(text.slice(0, caret));
  return m ? { start: m.index, partial: m[1]! } : null;
}

/** 用补全结果替换光标前的未完成引用；返回新文本与新光标位置 */
export function applyRef(text: string, caret: number, name: string): { text: string; caret: number } {
  const p = pendingRef(text, caret);
  if (!p) return { text, caret };
  // 光标后如果已经有 "}}"，不重复补
  const after = text.slice(caret);
  const tail = after.startsWith("}}") ? "" : "}}";
  const ins = `{{secret:${name}${tail}`;
  const next = text.slice(0, p.start) + ins + after;
  return { text: next, caret: p.start + ins.length + (tail ? 0 : 2) };
}

export function SecretRefInput({
  value,
  onChange,
  secrets,
  label,
  listLabel,
  testId,
}: {
  value: string;
  onChange(v: string): void;
  secrets: string[];
  label: string;
  listLabel: string;
  testId?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [caret, setCaret] = useState(0);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const pending = open ? pendingRef(value, caret) : null;
  const options = pending ? secrets.filter((s) => s.toLowerCase().startsWith(pending.partial.toLowerCase())) : [];
  const shown = !!pending && options.length > 0;

  const accept = (name: string) => {
    const r = applyRef(value, caret, name);
    onChange(r.text);
    setOpen(false);
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(r.caret, r.caret);
    });
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!shown) return;
    if (e.key === "ArrowDown") setActive((i) => (i + 1) % options.length);
    else if (e.key === "ArrowUp") setActive((i) => (i - 1 + options.length) % options.length);
    else if (e.key === "Enter" || e.key === "Tab") accept(options[Math.min(active, options.length - 1)]!);
    else if (e.key === "Escape") setOpen(false);
    else return;
    e.preventDefault();
  };
  return (
    <div className="secret-complete">
      <Input
        ref={ref}
        aria-label={label}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={shown}
        aria-controls={shown ? listId : undefined}
        aria-activedescendant={shown ? `${listId}-${Math.min(active, options.length - 1)}` : undefined}
        className="mono"
        value={value}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => {
          onChange(e.target.value);
          setCaret(e.target.selectionStart ?? e.target.value.length);
          setOpen(true);
          setActive(0);
        }}
        onSelect={(e) => setCaret((e.target as HTMLInputElement).selectionStart ?? 0)}
        onKeyDown={onKey}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        data-testid={testId}
      />
      {shown ? (
        <ul className="secret-complete__list" role="listbox" id={listId} aria-label={listLabel}>
          {options.map((s, i) => (
            <li
              key={s}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === Math.min(active, options.length - 1)}
              className="secret-complete__opt"
              onMouseDown={(e) => {
                e.preventDefault();
                accept(s);
              }}
              data-testid="secret-option"
            >
              {s}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
